import { ReactFlow, Background, Controls, Node, useViewport, Panel } from '@xyflow/react';
import { forwardRef, useImperativeHandle, useRef, useCallback, useEffect, useState } from 'react';
import '@xyflow/react/dist/style.css';
import { FloorPlanEditorProps, CustomNode } from '../types/FloorPlan';
import { useFloorPlanEditor } from '../hooks/useFloorPlanEditor';
import EquipmentNode from './nodes/EquipmentNode';
import { Percent } from 'lucide-react';
import { useFullscreen } from '@/contexts/FullscreenContext';

const nodeTypes = {
  equipment: EquipmentNode
};

const INITIAL_VIEWPORT = { x: 0, y: 0, zoom: 1 };
const LOCAL_STORAGE_KEY = 'floor-plan-editor-state';

const ZoomIndicator = () => {
  const { zoom } = useViewport();
  return (
    <div className="absolute left-2 bottom-16 bg-white border rounded-md text-sm flex items-center justify-center w-[72px] h-[24px]">
      {Math.round(zoom * 100)}
      <Percent className="h-3 w-3" />
    </div>
  );
};

const FloorPlanEditor = forwardRef<any, FloorPlanEditorProps>(({ zoom }, ref) => {
  const { isFullscreen } = useFullscreen();
  const containerRef = useRef<HTMLDivElement>(null);
  const reactFlowInstanceRef = useRef<any>(null);
  
  const {
    nodes,
    edges,
    selectedNodes,
    setSelectedNodes,
    mode,
    onNodesChange,
    saveToHistory,
    resetState,
    canUndo,
    canRedo,
    undo,
    redo,
    copySelectedNodes,
    pasteNodes,
    deleteSelectedNodes,
    hasUnsavedChanges,
    markAsSaved,
    loadSavedState
  } = useFloorPlanEditor();

  const [isInitialized, setIsInitialized] = useState(false);
  const [userZoom, setUserZoom] = useState<number | null>(null);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && nodes.length > 0) {
        console.log('Saving state to localStorage:', { nodes, edges });
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify({ 
          nodes, 
          edges,
          timestamp: Date.now() 
        }));
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Sla ook op bij het afsluiten van de pagina
    window.addEventListener('beforeunload', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleVisibilityChange);
    };
  }, [nodes, edges]);

  useEffect(() => {
    if (!isInitialized) {
      const savedState = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (savedState) {
        try {
          const { nodes: savedNodes, edges: savedEdges, timestamp } = JSON.parse(savedState);
          
          const isExpired = Date.now() - timestamp > 24 * 60 * 60 * 1000;
          
          if (!isExpired && savedNodes.length > 0) {
            console.log('Restoring state from localStorage:', { savedNodes, savedEdges });
            resetState(savedNodes, savedEdges);
          } else {
            console.log('Saved state is expired or empty, clearing localStorage');
            localStorage.removeItem(LOCAL_STORAGE_KEY);
          }
        } catch (error) {
          console.error('Error restoring state:', error);
          localStorage.removeItem(LOCAL_STORAGE_KEY);
        }
      }
      setIsInitialized(true);
    }
  }, [isInitialized, resetState]);

  useEffect(() => {
    if (isFullscreen && reactFlowInstanceRef.current) {
      setTimeout(() => {
        reactFlowInstanceRef.current.fitView({
          padding: 0.1,
          duration: 400,
          minZoom: 0.5,
          maxZoom: 1.5
        });
      }, 100);
    }
  }, [isFullscreen]);

  const handleNodeClick = useCallback((event: React.MouseEvent, clickedNode: Node) => {
    if (!clickedNode) return;
    
    const nativeEvent = event.nativeEvent as MouseEvent;
    const isMultiSelect = nativeEvent.ctrlKey || nativeEvent.metaKey;
    
    console.log('Node click:', { 
      node: clickedNode.id,
      ctrlKey: nativeEvent.ctrlKey,
      metaKey: nativeEvent.metaKey,
      isMultiSelect,
      currentSelection: selectedNodes.map(n => n.id).join(', '),
    });

    event.stopPropagation();

    if (isMultiSelect) {
      const isAlreadySelected = selectedNodes.some(n => n.id === clickedNode.id);
      
      if (isAlreadySelected) {
        console.log('Removing node from multi-selection:', clickedNode.id);
        setSelectedNodes(prev => prev.filter(n => n.id !== clickedNode.id));
      } else {
        console.log('Adding node to multi-selection:', clickedNode.id);
        setSelectedNodes(prev => [...prev, clickedNode as CustomNode]);
      }
    } else {
      console.log('Single-selecting node:', clickedNode.id);
      setSelectedNodes([clickedNode as CustomNode]);
    }
  }, [selectedNodes, setSelectedNodes]);

  const handlePaneClick = useCallback((event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      console.log('Pane click - clearing selection');
      setSelectedNodes([]);
    } else {
      console.log('Bubbled event - ignoring', event.target, event.currentTarget);
    }
  }, [setSelectedNodes]);

  const handleViewportChange = useCallback((event: any) => {
    const newZoom = event?.viewport?.zoom;
    if (newZoom !== undefined && newZoom !== userZoom) {
      setUserZoom(newZoom);
    }
  }, [userZoom]);

  useImperativeHandle(ref, () => ({
    containerRef,
    getNodes: () => nodes,
    getEdges: () => edges,
    setNodes: (newNodes: CustomNode[]) => {
      console.log('Setting new nodes:', newNodes);
      saveToHistory(newNodes, edges);
    },
    setEdges: (newEdges: any[]) => {
      console.log('Setting new edges:', newEdges);
      saveToHistory(nodes, newEdges);
    },
    resetState,
    canUndo: () => canUndo,
    canRedo: () => canRedo,
    undo,
    redo,
    rotateSelected: (degrees: number) => {
      if (selectedNodes.length === 0) return;
      
      const newNodes = nodes.map(node => {
        if (selectedNodes.find(selected => selected.id === node.id)) {
          const currentRotation = typeof node.data.rotation === 'number' ? node.data.rotation : 0;
          let newRotation = (currentRotation + degrees) % 360;
          if (newRotation < 0) newRotation += 360;
          
          return {
            ...node,
            data: {
              ...node.data,
              rotation: newRotation
            }
          };
        }
        return node;
      });
      
      saveToHistory(newNodes, edges);
    },
    flipSelectedHorizontal: () => {
      if (!selectedNodes.length) return;
      
      const newNodes = nodes.map(node => {
        if (selectedNodes.find(selected => selected.id === node.id)) {
          return {
            ...node,
            data: {
              ...node.data,
              flipX: !node.data.flipX
            }
          };
        }
        return node;
      });
      saveToHistory(newNodes, edges);
    },
    flipSelectedVertical: () => {
      if (!selectedNodes.length) return;
      
      const newNodes = nodes.map(node => {
        if (selectedNodes.find(selected => selected.id === node.id)) {
          return {
            ...node,
            data: {
              ...node.data,
              flipY: !node.data.flipY
            }
          };
        }
        return node;
      });
      saveToHistory(newNodes, edges);
    },
    duplicateSelected: () => {
      if (!selectedNodes.length) return;
      
      const newNodes = selectedNodes.map(selectedNode => ({
        ...selectedNode,
        id: `${selectedNode.id}-copy-${Date.now()}`,
        position: {
          x: selectedNode.position.x + 20,
          y: selectedNode.position.y + 20
        }
      }));
      saveToHistory([...nodes, ...newNodes], edges);
    },
    deleteSelected: deleteSelectedNodes,
    sendSelectedToBack: () => {
      if (!selectedNodes.length) return;
    
      const newNodes = nodes.map(node => {
        if (selectedNodes.find(selected => selected.id === node.id)) {
          return {
            ...node,
            zIndex: 0,
            data: {
              ...node.data,
              zIndex: 0
            }
          };
        }
        return {
          ...node,
          zIndex: (node.zIndex || 0) + 1,
          data: {
            ...node.data,
            zIndex: (node.data.zIndex || 0) + 1
          }
        };
      });
      saveToHistory(newNodes, edges);
    },
    bringSelectedToFront: () => {
      if (!selectedNodes.length) return;
    
      const maxZIndex = Math.max(...nodes.map(node => node.zIndex || 0));
    
      const newNodes = nodes.map(node => {
        if (selectedNodes.find(selected => selected.id === node.id)) {
          return {
            ...node,
            zIndex: maxZIndex + 1,
            data: {
              ...node.data,
              zIndex: maxZIndex + 1
            }
          };
        }
        return node;
      });
      saveToHistory(newNodes, edges);
    },
    hasUnsavedChanges,
    markAsSaved,
    loadSavedState,
    fitViewport: () => {
      if (reactFlowInstanceRef.current) {
        console.log('Fitting view to all nodes');
        reactFlowInstanceRef.current.fitView({ 
          padding: 0.2, 
          duration: 400,
          minZoom: 0.5,
          maxZoom: 1.5
        });
        return true;
      }
      console.log('No ReactFlow instance available');
      return false;
    }
  }), [nodes, edges, selectedNodes, saveToHistory, canUndo, canRedo, undo, redo, deleteSelectedNodes, hasUnsavedChanges, markAsSaved, loadSavedState]);

  return (
    <div className={`w-full h-full bg-background ${isFullscreen ? 'fixed inset-0 z-50' : ''}`} id="floor-plan-container" ref={containerRef}>
      <ReactFlow
        nodes={nodes.map(node => ({
          ...node,
          style: {
            ...node.style,
            width: node.data.isImage ? 100 : 50,
            height: node.data.isImage ? 100 : 50,
          },
          selected: selectedNodes.some(selectedNode => selectedNode.id === node.id),
        }))}
        edges={edges}
        onNodesChange={onNodesChange}
        onNodeClick={handleNodeClick}
        onPaneClick={handlePaneClick}
        nodeTypes={nodeTypes}
        panOnScroll={mode === 'pan'}
        panOnDrag={mode === 'pan'}
        zoomOnScroll={false}
        zoomOnPinch={false}
        zoomOnDoubleClick={false}
        selectNodesOnDrag={false}
        multiSelectionKeyCode={null}
        selectionKeyCode={null}
        deleteKeyCode={null}
        defaultViewport={INITIAL_VIEWPORT}
        minZoom={0.1}
        maxZoom={4}
        fitView={false}
        onMove={handleViewportChange}
        proOptions={{ hideAttribution: true }}
        onInit={(instance) => {
          console.log('ReactFlow instance initialized');
          reactFlowInstanceRef.current = instance;
        }}
      >
        <Background />
        <Controls 
          className="!left-2 !bottom-28"
          showZoom={true}
          showFitView={true}
          fitViewOptions={{ 
            padding: 0.2, 
            duration: 200,
            minZoom: 1,
            maxZoom: 1
          }}
        />
        <ZoomIndicator />
      </ReactFlow>
    </div>
  );
});

FloorPlanEditor.displayName = 'FloorPlanEditor';

export default FloorPlanEditor;
